/**
 * copy.ts — the small, DOCUMENTED vocabulary for user-facing microcopy, plus the plain failure/holding
 * copy and a snake_case token humanizer. One word per provenance state so the same phrase always means
 * the same thing on every screen (DISP honesty: an empty surface is never a silent zero; a REAL-empty
 * field is confirmed-absent, a DEFERRED field is still on its way).
 *
 * This is a PRESENTATION helper ONLY. It changes no data, no count, and no provenance tier (deriveBadge
 * still owns the tiers) - it only standardizes how those states are WORDED. Keep it free of HTTP status
 * codes, table/field/endpoint names, stack text, "feed-gated", null/undefined: those stay in LOGS (with
 * the traceId/correlation id), never on screen.
 */

/**
 * The microcopy vocabulary, tied to the deriveBadge tiers:
 *   - fieldAbsent     : a single field genuinely absent in a REAL row (confirmed not recorded).
 *   - collectionEmpty : a REAL collection/surface that is complete but holds nothing.
 *   - awaitingFeed    : a field/surface whose feed has not landed yet (DEFERRED; withheld, never a zero).
 *   - notApplicable   : structurally not applicable to this population/context.
 */
export const MICROCOPY = {
  fieldAbsent: "Not recorded",
  collectionEmpty: "None on record",
  awaitingFeed: "Awaiting feed",
  notApplicable: "Not applicable",
} as const;

/**
 * Plain, reassuring copy for failures and the DEFERRED holding state. No codes, no table names, no
 * jargon: the meaning (an empty surface is waiting on data, not a real zero) is preserved in clinician
 * language. Diagnostic detail (traceId, status) stays in the logger, never in these strings.
 */
export const COPY = {
  /** Generic load failure shown to the user. */
  loadError: "We could not load this right now. Please try again.",
  /** The DEFERRED holding state body: an empty surface is awaiting data, not a real zero. */
  awaitingFeedHolding: "Awaiting feed. This space is waiting on data, not a zero.",
} as const;

// Known acronyms / multi-word terms that must not be naively title-cased. Keyed by the lower-case token
// (whole value first, then per-word) so "behavioral_health" -> "Behavioral Health" and "bmi" -> "BMI".
const TERM_OVERRIDES: Record<string, string> = {
  behavioral_health: "Behavioral Health",
  bmi: "BMI",
  copd: "COPD",
  ckd: "CKD",
  chf: "CHF",
  cad: "CAD",
  raf: "RAF",
  hiv: "HIV",
  hba1c: "HbA1c",
};

/**
 * humanizeToken — turn a snake_case / lower_case domain token into a clinician-friendly label
 * ("behavioral_health" -> "Behavioral Health"). A whole-value override wins first; otherwise each word is
 * title-cased (known acronyms kept). A value that is already human (has a space OR an uppercase letter,
 * e.g. "Commercial", "Self-Pay") is returned unchanged. Null/empty -> the "Not recorded" vocabulary word.
 */
export function humanizeToken(value: string | null | undefined): string {
  if (value == null || value === "") return MICROCOPY.fieldAbsent;
  const key = value.toLowerCase();
  if (TERM_OVERRIDES[key]) return TERM_OVERRIDES[key];
  // Already human-readable (a space or any uppercase) -> leave as authored.
  if (/\s/.test(value) || /[A-Z]/.test(value)) return value;
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => TERM_OVERRIDES[w] ?? w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}
